import { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useGet } from 'hooks';
import Actions from './Actions/ActionsV2';
import Members from './Members';
import {
  setActiveList,
  setListMembers,
  setModal,
  setView,
  toggleLists,
} from 'actions/lists';
import {
  getActiveModal,
  getPageNumber,
  getPageSize,
  getTableSort,
  getActiveList,
  getMembersSearch,
  getIsLoading,
} from 'selectors/lists';
import { getList, getMembersForList } from 'services/api';
import { views } from './constants';
import useAddMembersAndRefetch from './hooks/useAddMembersAndRefetch';
import ListDescription from 'components/common/Lists/ListDescription';
import { Pane } from 'components/common';
import { Create } from 'components/common/Lists/Modals';
import { Edit, Delete, AddTo, Export, RemoveFrom, PushToCrm } from './Modals';
import EnrichmentIcon from 'components/common/Icon/EnrichmentIcon';
import styled from 'styled-components';

const EnrichTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const modals = {
  create: Create,
  createAndAdd: Create,
  edit: Edit,
  delete: Delete,
  addTo: AddTo,
  export: Export,
  removeFrom: RemoveFrom,
  pushToCrm: PushToCrm,
};

const List = ({ view = views.organisations }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const activeList = useSelector(getActiveList);
  const pageNumber = useSelector(getPageNumber);
  const pageSize = useSelector(getPageSize);
  const sort = useSelector(getTableSort);
  const history = useHistory();

  const activeModal = useSelector(getActiveModal);

  const [{ res: list, loading: getListLoading, error: getListError }] = useGet({
    url: getList(id, view),
    onMount: true,
  });

  useEffect(() => {
    dispatch(setView(view));
  }, [dispatch, view]);

  useEffect(() => {
    if (list) {
      dispatch(setActiveList(list));
    }
  }, [dispatch, list]);

  useEffect(() => {
    if (getListError) {
      dispatch(setActiveList({}));
    }
  }, [dispatch, getListError]);

  const search = useSelector(getMembersSearch);
  const [
    { res: fetchListMembersResponse, loading: fetchListMembersLoading },
    fetchListMembers,
  ] = useGet({
    url: getMembersForList(id, view),
    query: {
      pageSize,
      pageNumber,
      orderBy: sort.by,
      isAscending: sort.dir === 'asc',
      search,
    },
    onMount: true,
  });

  const [addingMembersLoading, handleAddMembers] = useAddMembersAndRefetch(
    activeList?.id,
    useCallback(() => null, []),
    view,
    false,
    useCallback(
      ({ id }) => {
        history.replace(
          `/lists/${
            view === views.organisations ? 'organisation' : 'researcher'
          }/${id}`
        );
      },
      [view, history]
    )
  );

  useEffect(() => {
    if (fetchListMembersResponse) {
      dispatch(setListMembers(fetchListMembersResponse));
    }
  }, [dispatch, fetchListMembersResponse]);

  const onModalClose = options => {
    if (options?.id && activeModal === 'edit') {
      const { fetchLists, fetchMembers, ...updatedList } = options;
      dispatch(setActiveList(updatedList));
    }
    if (options?.id && activeModal === 'createAndAdd') {
      handleAddMembers({ id: options.id });
    }
    if (options?.fetchMembers && activeModal === 'removeFrom') {
      fetchListMembers();
    }
    if (options?.deleteListSuccessfully) {
      if (options?.id) {
        dispatch(toggleLists({ id: options.id, onlyIfSelected: true }));
      }
      history.replace('/lists');
    }

    dispatch(setModal(''));
  };

  const crmTypeRef = useRef(null);
  const handlePushToCRM = useCallback(
    (crmType, list) => {
      crmTypeRef.current = crmType;
      dispatch(setModal('pushToCrm'));
    },
    [dispatch]
  );

  const title = getListLoading ? (
    ''
  ) : (
    <EnrichTitle>
      <div>{activeList?.name}</div>
      {activeList?.dataEnrichmentId && <EnrichmentIcon />}
    </EnrichTitle>
  );

  const isGlobalLoading = useSelector(getIsLoading);

  const Modal = modals[activeModal];
  const isLoading =
    getListLoading ||
    addingMembersLoading ||
    fetchListMembersLoading ||
    isGlobalLoading;
  return (
    <Pane title={title}>
      {activeList?.description && !getListLoading && (
        <ListDescription description={activeList.description} />
      )}
      <Actions
        loading={isLoading}
        hasIndustryLicense={true}
        view={view}
        onPushToCRM={handlePushToCRM}
        style={{ marginTop: 15, marginBottom: 15 }}
        placeholder={`Search for ${
          view === views.researchers ? 'a Contact' : 'an Organisation'
        }`}
      />
      <Members
        onCreateNewItemList={() => {
          dispatch(setModal('createAndAdd'));
        }}
        loading={isLoading}
        view={view}
      />
      {activeModal && (
        <Modal
          onClose={onModalClose}
          shouldSetActiveList={activeModal !== 'createAndAdd'}
          type={view}
          crmType={crmTypeRef.current}
        />
      )}
    </Pane>
  );
};

List.propTypes = {
  view: PropTypes.oneOf(Object.values(views)),
};

List.defaultProps = {
  lists: [],
};

export default List;
